import React, { useEffect, useState } from 'react';
import { BadgeCheckIcons, ChevronDownIcons, FilterIcons, SearchIcons, XIcons } from '../../../icons/icons';

export interface FilterOptions {
  skills: string[];
  locations: string[];
  experienceLevels: string[];
  status: string[];
  matchRange: [number, number];
  availability: string[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
  onClearFilters: () => void;
  availableSkills: string[];
  availableLocations: string[];
}

const EXPERIENCE_LEVELS = [
  { value: '0-2', label: '0-2 years' },
  { value: '2-5', label: '2-5 years' },
  { value: '5-8', label: '5-8 years' },
  { value: '8+', label: '8+ years' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open to roles', color: 'emerald' },
  { value: 'active', label: 'Actively looking', color: 'blue' },
  { value: 'unavailable', label: 'Not available', color: 'slate' },
];

const AVAILABILITY_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: '2-weeks', label: 'Within 2 weeks' },
  { value: '1-month', label: 'Within 1 month' },
  { value: '3-months', label: 'Within 3 months' },
];

const SORT_OPTIONS = [
  { value: 'matchPercentage', label: 'Match Score' },
  { value: 'name', label: 'Name' },
  { value: 'experience', label: 'Experience' },
  { value: 'status', label: 'Status' },
  { value: 'location', label: 'Location' },
];

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  onClearFilters,
  availableSkills,
  availableLocations,
}) => {
  const [localFilters, setLocalFilters] = useState<FilterOptions>(filters);
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    skills: true,
    location: true,
    experience: true,
    status: true,
    match: true,
    availability: false,
    sort: false,
  });

  const [skillSearch, setSkillSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleFilterUpdate = (key: keyof FilterOptions, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
    onClose();
  };

  const handleClearAll = () => {
    setLocalFilters({
      skills: [],
      locations: [],
      experienceLevels: [],
      status: [],
      matchRange: [0, 100],
      availability: [],
      sortBy: 'matchPercentage',
      sortOrder: 'desc',
    });
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleSkillToggle = (skill: string) => {
    const newSkills = localFilters.skills.includes(skill)
      ? localFilters.skills.filter(s => s !== skill)
      : [...localFilters.skills, skill];
    handleFilterUpdate('skills', newSkills);
  };

  const handleLocationToggle = (location: string) => {
    const newLocations = localFilters.locations.includes(location)
      ? localFilters.locations.filter(l => l !== location)
      : [...localFilters.locations, location];
    handleFilterUpdate('locations', newLocations);
  };

  const handleStatusToggle = (status: string) => {
    const newStatus = localFilters.status.includes(status)
      ? localFilters.status.filter(s => s !== status)
      : [...localFilters.status, status];
    handleFilterUpdate('status', newStatus);
  };

  const filteredSkills = availableSkills.filter(skill =>
    skill.toLowerCase().includes(skillSearch.toLowerCase()),
  );

  const filteredLocations = availableLocations.filter(location =>
    location.toLowerCase().includes(locationSearch.toLowerCase()),
  );

  const getActiveFiltersCount = () => {
    return localFilters.skills.length +
           localFilters.locations.length +
           localFilters.experienceLevels.length +
           localFilters.status.length +
           localFilters.availability.length +
           (localFilters.matchRange[0] > 0 || localFilters.matchRange[1] < 100 ? 1 : 0);
  };

  const Section: React.FC<{
    title: string;
    section: string;
    count?: number;
    children: React.ReactNode;
  }> = ({ title, section, count, children }) => (
    <div className='border-b border-slate-200/60 last:border-0'>
      <button
          onClick={() => toggleSection(section)}
          className='w-full flex items-center justify-between p-4 text-left hover:bg-slate-50/50 transition-colors'
      >
        <div className='flex items-center gap-3'>
          <span className='font-semibold text-slate-900'>{ title }</span>
          { count !== undefined && count > 0 && (
            <span className='bg-indigo-100 text-indigo-700 text-xs font-medium px-2 py-1 rounded-full'>
              { count }
            </span>
          ) }
        </div>
        <ChevronDownIcons
            className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${
              expandedSections[section] ? 'rotate-180' : ''
            }`}
        />
      </button>
      { expandedSections[section] && (
        <div className='px-4 pb-4'>
          { children }
        </div>
      ) }
    </div>
  );

  return (
    <div
        className={`fixed inset-0 z-50 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300 ease-in-out`}
        onClick={onClose}
    >
      { /* Backdrop */ }
      <div className='absolute inset-0 bg-black/30 backdrop-blur-sm' />

      { /* Drawer */ }
      <div
          className={`fixed top-0 right-0 h-full w-96 bg-white shadow-2xl transform ${
            isOpen ? 'translate-x-0' : 'translate-x-full'
          } transition-transform duration-300 ease-in-out overflow-hidden flex flex-col`}
          onClick={(e) => e.stopPropagation()}
      >
        { /* Header */ }
        <div className='bg-gradient-to-r from-indigo-600 to-indigo-700 p-6 text-white'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-3'>
              <div className='w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center'>
                <FilterIcons className='w-4 h-4' />
              </div>
              <div>
                <h2 className='text-lg font-bold'>Advanced Filters</h2>
                <p className='text-indigo-200 text-sm'>
                  { getActiveFiltersCount() } active filters
                </p>
              </div>
            </div>
            <button
                onClick={onClose}
                className='p-2 hover:bg-white/20 rounded-lg transition-colors'
            >
              <XIcons className='w-5 h-5' />
            </button>
          </div>
        </div>

        { /* Content */ }
        <div className='flex-1 overflow-y-auto'>
          { /* Skills Filter */ }
          <Section title='Skills' section='skills' count={localFilters.skills.length}>
            <div className='space-y-3'>
              <div className='relative'>
                <SearchIcons className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4' />
                <input
                    type='text'
                    placeholder='Search skills...'
                    value={skillSearch}
                    onChange={(e) => setSkillSearch(e.target.value)}
                    className='w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                />
              </div>
              <div className='max-h-40 overflow-y-auto space-y-2'>
                { filteredSkills.map(skill => (
                  <label key={skill} className='flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer'>
                    <div className='relative'>
                      <input
                          type='checkbox'
                          checked={localFilters.skills.includes(skill)}
                          onChange={() => handleSkillToggle(skill)}
                          className='sr-only'
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        localFilters.skills.includes(skill)
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}>
                        { localFilters.skills.includes(skill) && <BadgeCheckIcons className='w-3 h-3' /> }
                      </div>
                    </div>
                    <span className='text-sm text-slate-700'>{ skill }</span>
                  </label>
                )) }
              </div>
            </div>
          </Section>

          { /* Location Filter */ }
          <Section title='Location' section='location' count={localFilters.locations.length}>
            <div className='space-y-3'>
              <div className='relative'>
                <SearchIcons className='absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4' />
                <input
                    type='text'
                    placeholder='Search locations...'
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className='w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
                />
              </div>
              <div className='max-h-40 overflow-y-auto space-y-2'>
                { filteredLocations.map(location => (
                  <label key={location} className='flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer'>
                    <div className='relative'>
                      <input
                          type='checkbox'
                          checked={localFilters.locations.includes(location)}
                          onChange={() => handleLocationToggle(location)}
                          className='sr-only'
                      />
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        localFilters.locations.includes(location)
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}>
                        { localFilters.locations.includes(location) && <BadgeCheckIcons className='w-3 h-3' /> }
                      </div>
                    </div>
                    <span className='text-sm text-slate-700'>{ location }</span>
                  </label>
                )) }
              </div>
            </div>
          </Section>

          { /* Experience Level Filter */ }
          <Section title='Experience Level' section='experience' count={localFilters.experienceLevels.length}>
            <div className='space-y-2'>
              { EXPERIENCE_LEVELS.map(exp => (
                <label key={exp.value} className='flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer'>
                  <div className='relative'>
                    <input
                        type='checkbox'
                        checked={localFilters.experienceLevels.includes(exp.value)}
                        onChange={() => {
                          const newLevels = localFilters.experienceLevels.includes(exp.value)
                            ? localFilters.experienceLevels.filter(l => l !== exp.value)
                            : [...localFilters.experienceLevels, exp.value];
                          handleFilterUpdate('experienceLevels', newLevels);
                        }}
                        className='sr-only'
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      localFilters.experienceLevels.includes(exp.value)
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-300 bg-white'
                    }`}>
                      { localFilters.experienceLevels.includes(exp.value) && <BadgeCheckIcons className='w-3 h-3' /> }
                    </div>
                  </div>
                  <span className='text-sm text-slate-700'>{ exp.label }</span>
                </label>
              )) }
            </div>
          </Section>

          { /* Status Filter */ }
          <Section title='Status' section='status' count={localFilters.status.length}>
            <div className='space-y-2'>
              { STATUS_OPTIONS.map(status => (
                <label key={status.value} className='flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer'>
                  <div className='relative'>
                    <input
                        type='checkbox'
                        checked={localFilters.status.includes(status.value)}
                        onChange={() => handleStatusToggle(status.value)}
                        className='sr-only'
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      localFilters.status.includes(status.value)
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-300 bg-white'
                    }`}>
                      { localFilters.status.includes(status.value) && <BadgeCheckIcons className='w-3 h-3' /> }
                    </div>
                  </div>
                  <div className='flex items-center gap-2'>
                    <div className={`w-2 h-2 rounded-full bg-${status.color}-500`} />
                    <span className='text-sm text-slate-700'>{ status.label }</span>
                  </div>
                </label>
              )) }
            </div>
          </Section>

          { /* Match Score Range */ }
          <Section title='Match Score' section='match'>
            <div className='space-y-4'>
              <div className='px-3'>
                <input
                    type='range'
                    min={0}
                    max={100}
                    value={localFilters.matchRange[1]}
                    onChange={(e) => handleFilterUpdate('matchRange', [localFilters.matchRange[0], parseInt(e.target.value)])}
                    className='w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer'
                />
                <div className='flex justify-between text-sm text-slate-600 mt-2'>
                  <span>{ localFilters.matchRange[0] }%</span>
                  <span>{ localFilters.matchRange[1] }%</span>
                </div>
              </div>
              <div className='flex items-center gap-2 text-sm text-slate-600'>
                <span>Showing candidates with</span>
                <span className='font-semibold text-indigo-600'>
                  { localFilters.matchRange[0] }%-{ localFilters.matchRange[1] }%
                </span>
                <span>match score</span>
              </div>
            </div>
          </Section>

          { /* Availability Filter */ }
          <Section title='Availability' section='availability' count={localFilters.availability.length}>
            <div className='space-y-2'>
              { AVAILABILITY_OPTIONS.map(availability => (
                <label key={availability.value} className='flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer'>
                  <div className='relative'>
                    <input
                        type='checkbox'
                        checked={localFilters.availability.includes(availability.value)}
                        onChange={() => {
                          const newAvailability = localFilters.availability.includes(availability.value)
                            ? localFilters.availability.filter(a => a !== availability.value)
                            : [...localFilters.availability, availability.value];
                          handleFilterUpdate('availability', newAvailability);
                        }}
                        className='sr-only'
                    />
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      localFilters.availability.includes(availability.value)
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : 'border-slate-300 bg-white'
                    }`}>
                      { localFilters.availability.includes(availability.value) && <BadgeCheckIcons className='w-3 h-3' /> }
                    </div>
                  </div>
                  <span className='text-sm text-slate-700'>{ availability.label }</span>
                </label>
              )) }
            </div>
          </Section>

          { /* Sort Options */ }
          <Section title='Sort By' section='sort'>
            <div className='space-y-3'>
              <select
                  value={localFilters.sortBy}
                  onChange={(e) => handleFilterUpdate('sortBy', e.target.value)}
                  className='w-full p-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent'
              >
                { SORT_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{ option.label }</option>
                )) }
              </select>
              <div className='flex items-center gap-4'>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                      type='radio'
                      name='sortOrder'
                      value='desc'
                      checked={localFilters.sortOrder === 'desc'}
                      onChange={(e) => handleFilterUpdate('sortOrder', e.target.value as 'desc')}
                      className='text-indigo-600 focus:ring-indigo-500'
                  />
                  <span className='text-sm text-slate-700'>High to Low</span>
                </label>
                <label className='flex items-center gap-2 cursor-pointer'>
                  <input
                      type='radio'
                      name='sortOrder'
                      value='asc'
                      checked={localFilters.sortOrder === 'asc'}
                      onChange={(e) => handleFilterUpdate('sortOrder', e.target.value as 'asc')}
                      className='text-indigo-600 focus:ring-indigo-500'
                  />
                  <span className='text-sm text-slate-700'>Low to High</span>
                </label>
              </div>
            </div>
          </Section>
        </div>

        { /* Footer */ }
        <div className='border-t border-slate-200 p-4 bg-white'>
          <div className='flex items-center gap-3'>
            <button
                onClick={handleClearAll}
                className='flex-1 px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors'
            >
              Clear All
            </button>
            <button
                onClick={handleApplyFilters}
                className='flex-1 px-4 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all'
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
